import { apiClient } from "@shared/lib/api-client";

/**
 * 直接上传图片到 R2，不经过 Vercel 服务器
 * @param file 压缩后的图片文件
 * @returns R2 公开 URL
 */
export async function uploadImageToR2Directly(file: File): Promise<string> {
	try {
		// 1. 请求 presigned URL（只传 contentType，后端生成路径）
		const response = await apiClient.uploads["signed-upload-url-r2"].$post({
			json: {
				contentType: file.type || "image/jpeg",
			},
		});

		if (!response.ok) {
			const errorText = await response.text();
			throw new Error(
				`Failed to get presigned URL: ${response.status} ${errorText}`,
			);
		}

		const { signedUrl, publicUrl } = await response.json();

		// 2. 直接上传到 R2（不经过 Vercel）
		const uploadResponse = await fetch(signedUrl, {
			method: "PUT",
			body: file,
			headers: {
				"Content-Type": file.type || "image/jpeg",
			},
		});

		if (!uploadResponse.ok) {
			throw new Error(
				`Failed to upload to R2: ${uploadResponse.status} ${uploadResponse.statusText}`,
			);
		}

		// 3. 登记到 My Assets。匿名用户会 401，失败也不该挡住上传流程
		try {
			const recordResponse = await apiClient.uploads.record.$post({
				json: { imageUrl: publicUrl },
			});
			// hono client 对 4xx/5xx 不 throw。不看 ok 的话登记失败是完全静默的：
			// 图进了 R2，却永远不出现在 My Assets，前端一点动静都没有
			if (!recordResponse.ok) {
				console.error(
					"Failed to record upload:",
					recordResponse.status,
					await recordResponse.text(),
				);
			}
		} catch (error) {
			console.error("Error recording upload:", error);
		}

		// 4. 返回公开 URL
		return publicUrl;
	} catch (error) {
		console.error("Error uploading to R2:", error);
		throw error;
	}
}

/**
 * 参考视频 / 参考音频直传 R2。和图片同一个 presign 接口，但不登记到 My Assets（素材库只放图）
 * @param file 已经转码好的 mp4 或 wav（见 normalize-reference-media.ts）
 * @returns R2 公开 URL
 */
export async function uploadReferenceMediaToR2Directly(
	file: File,
): Promise<string> {
	const response = await apiClient.uploads["signed-upload-url-r2"].$post({
		json: { contentType: file.type },
	});
	if (!response.ok) {
		throw new Error(
			`Failed to get presigned URL: ${response.status} ${await response.text()}`,
		);
	}
	const { signedUrl, publicUrl } = await response.json();

	const uploadResponse = await fetch(signedUrl, {
		method: "PUT",
		body: file,
		headers: { "Content-Type": file.type },
	});
	if (!uploadResponse.ok) {
		throw new Error(
			`Failed to upload to R2: ${uploadResponse.status} ${uploadResponse.statusText}`,
		);
	}
	return publicUrl;
}

/**
 * 批量上传图片到 R2
 * @param files 压缩后的图片文件数组
 * @returns R2 公开 URL 数组
 */
export async function uploadImagesToR2Directly(
	files: File[],
): Promise<string[]> {
	const uploadPromises = files.map((file) => uploadImageToR2Directly(file));
	return Promise.all(uploadPromises);
}
